import { STATUS_LABELS, EVENT_TYPE_LABELS } from "./schema";
import { permitUrgency } from "./utils/permits";

const STATUS_COLORS = {
  new_inquiry:      { bg: "#4aa4e1", fg: "#0a1a2e" },
  contacted:        { bg: "#b9d550", fg: "#0d1833" },
  quoted:           { bg: "#fcb31f", fg: "#0d1833" },
  deposit_received: { bg: "#f57600", fg: "#ffffff" },
  booked:           { bg: "#1f59db", fg: "#ffffff" },
  completed:        { bg: "#234080", fg: "#ffdaad" },
  declined:         { bg: "#6b2234", fg: "#ffdaad" },
  no_response:      { bg: "#6b2234", fg: "#ffdaad" },
  overdue:          { bg: "#ef537e", fg: "#ffffff" },
  payment_failed:   { bg: "#ef537e", fg: "#ffffff" },
  spam:             { bg: "#3a1424", fg: "rgba(255,255,255,0.5)" },
  hold:             { bg: "#8a6a00", fg: "#ffdaad" },
};

function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  if (typeof ts === "string") return new Date(ts);
  if (typeof ts === "number") return new Date(ts);
  return null;
}

function fmtDateCol(ts) {
  const d = toDate(ts);
  if (!d) return { top: "TBD", bottom: "" };
  const top = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const bottom = d.toLocaleDateString("en-US", { year: "numeric", weekday: "short" });
  return { top, bottom };
}

function fmtMoney(n) {
  if (n == null || n === "") return "";
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (!Number.isFinite(num)) return "";
  return "$" + num.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export default function BookingCard({ booking, onOpen, onArchiveToggle, onDelete, selectionMode, selected, onToggleSelect }) {
  const dateSource = booking.confirmedDate || booking.inquiryEventDate;
  const dateCol = fmtDateCol(dateSource);
  const statusColor = STATUS_COLORS[booking.status] || STATUS_COLORS.new_inquiry;
  const rate = fmtMoney(booking.totalRate) || fmtMoney(booking.quotedAmount);
  const permit = permitUrgency(booking);
  const isArchived = Boolean(booking.archivedAt);

  const handleClick = () => {
    if (selectionMode) onToggleSelect?.(booking.id);
    else onOpen(booking.id);
  };

  const stop = (fn) => (e) => { e.stopPropagation(); fn?.(); };

  return (
    <div
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); } }}
      style={{
        display: "grid",
        gridTemplateColumns: selectionMode ? "auto 80px 1fr auto auto" : "80px 1fr auto auto",
        gap: "0.75rem",
        alignItems: "center",
        width: "100%",
        background: selected ? "#1a3068" : "#142548",
        border: `1px solid ${selected ? "#fcb31f" : "#234080"}`,
        borderRadius: "10px",
        padding: "0.85rem 1rem",
        cursor: "pointer",
        textAlign: "left",
        color: "white",
        boxSizing: "border-box",
      }}
    >
      {selectionMode && (
        <input
          type="checkbox"
          checked={!!selected}
          onChange={() => onToggleSelect?.(booking.id)}
          onClick={(e) => e.stopPropagation()}
          style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#fcb31f" }}
        />
      )}
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fcb31f" }}>{dateCol.top}</div>
        <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)" }}>{dateCol.bottom}</div>
      </div>

      <div style={{ minWidth: 0 }}>
        <div
          title={booking.eventName || booking.name || ""}
          style={{
            fontWeight: 700,
            fontSize: "1rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {booking.eventName || booking.name || "(no name)"}
        </div>
        <div
          title={[booking.venueName, booking.location, booking.attendance ? `${booking.attendance} guests` : null]
            .filter(Boolean).join(" · ") || (booking.eventName ? booking.name : booking.email || "")}
          style={{
            fontSize: "0.78rem",
            color: "rgba(255,255,255,0.7)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {[booking.venueName, booking.location, booking.attendance ? `${booking.attendance} guests` : null]
            .filter(Boolean).join(" · ") || (booking.eventName ? booking.name : booking.email || "")}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.3rem" }}>
        <span style={{
          background: statusColor.bg,
          color: statusColor.fg,
          borderRadius: "999px",
          padding: "0.2rem 0.6rem",
          fontSize: "0.7rem",
          fontWeight: 700,
          letterSpacing: "0.02em",
          whiteSpace: "nowrap",
        }}>
          {STATUS_LABELS[booking.status] || "—"}
        </span>
        <div style={{ display: "flex", gap: "0.35rem", fontSize: "0.7rem", color: "rgba(255,255,255,0.7)", alignItems: "center" }}>
          {booking.isPublicEvent && (
            <span title="Listed on the public events calendar"
              style={{ background: "rgba(252,179,31,0.18)", color: "#fcb31f", border: "1px solid rgba(252,179,31,0.45)", borderRadius: "6px", padding: "0.1rem 0.4rem", fontWeight: 700, fontSize: "0.65rem", letterSpacing: "0.03em" }}>
              📅 Public
            </span>
          )}
          {permit === "urgent" && (
            <span style={{ background: "#c94444", color: "white", borderRadius: "6px", padding: "0.1rem 0.4rem", fontWeight: 800, fontSize: "0.65rem", letterSpacing: "0.03em" }}>
              ⚠ PERMIT
            </span>
          )}
          {permit === "warning" && (
            <span style={{ background: "#f57600", color: "white", borderRadius: "6px", padding: "0.1rem 0.4rem", fontWeight: 800, fontSize: "0.65rem", letterSpacing: "0.03em" }}>
              📋 permit
            </span>
          )}
          {booking.eventType ? <span>{EVENT_TYPE_LABELS[booking.eventType] || booking.eventType}</span> : null}
          {rate ? <span style={{ color: "#fcb31f", fontWeight: 700 }}>{rate}</span> : null}
          {booking._taskCounts?.overdue > 0 && (
            <span style={{ background: "#c94444", color: "white", borderRadius: "6px", padding: "0.1rem 0.4rem", fontWeight: 800, fontSize: "0.65rem" }}>
              ⚠ {booking._taskCounts.overdue} overdue
            </span>
          )}
          {!booking._taskCounts?.overdue && booking._taskCounts?.pending > 0 && (
            <span style={{ background: "rgba(74,164,225,0.15)", color: "#4aa4e1", borderRadius: "6px", padding: "0.1rem 0.4rem", fontWeight: 700, fontSize: "0.65rem" }}>
              {booking._taskCounts.pending} task{booking._taskCounts.pending === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>
      {!selectionMode && (
        <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
          <button
            type="button"
            onClick={stop(onArchiveToggle)}
            title={isArchived ? "Unarchive" : "Archive"}
            style={iconBtnStyle}
          >
            {isArchived ? "↩" : "📦"}
          </button>
          <button
            type="button"
            onClick={stop(onDelete)}
            title="Delete permanently"
            style={{ ...iconBtnStyle, color: "#ef537e" }}
          >
            🗑
          </button>
        </div>
      )}
    </div>
  );
}

const iconBtnStyle = {
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "rgba(255,255,255,0.85)",
  borderRadius: "6px",
  width: "30px",
  height: "30px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: "0.85rem",
  padding: 0,
};
